/**
 * Front-door copy (#3123, child of the #3122 marketing epic).
 *
 * WHY THIS IS A DATA MODULE AND NOT INLINE JSX
 * --------------------------------------------
 * `scripts/check-voice.mjs` scans `frontend/src/data/content/*.ts`. Inline JSX
 * strings are inside `oro-voice`'s stated scope but OUTSIDE the gate's roots
 * (#2954), so copy written straight into `HomePage.tsx` gets no mechanical
 * check for em-dashes or British spellings. Putting it here means the gate is
 * real for this page from the first commit rather than after #2954 lands.
 *
 * NOT a `ContentDoc`. The legal and About/FAQ pages are documents rendered by
 * `ContentDocView`; this is a marketing layout with fixed slots, so it is a
 * flat typed record. `brandCadence.test.ts` walks ContentDoc shapes only and
 * does not see this file — the ™ pin for this page lives in
 * `HomePage.landing.test.tsx`, which counts rendered glyphs instead.
 *
 * THE ™ IS NOT IN THIS FILE, DELIBERATELY.
 * Core Rule 24 gives the page exactly one mark, on its most prominent use. That
 * is the hero subhead, and it renders through `<OroTrademark />` so the mark
 * stays a component rather than a hardcoded glyph. The string here is only the
 * remainder of that sentence — see `heroSubheadAfterBrand`.
 *
 * DEMO DATA ONLY. Section 3 shows the product using the seeded Golden Hour
 * club. Never a real club, never a member's name or face (#3122 imagery rule).
 */

export const LANDING = {
  /** Section 2 — hero. */
  hero: {
    headline: 'Make a plan.\nShare it.\nMove together.',
    /**
     * Rendered as `<OroTrademark /> {heroSubheadAfterBrand}`, so the sentence
     * reads "ORO move™ is where your club posts…". Naming the product here is
     * what closes the B3 finding: the swap test showed the headline could not
     * carry "what is this" alone once the full-bleed photograph was removed.
     */
    heroSubheadAfterBrand: 'gives your club a place for events, RSVPs, updates, check-ins, and conversation, so details don\'t get in the way.',
    primaryCta: 'See how it works',
    secondaryCta: 'Sign in',
  },

  /** Section 3 — the week, shown. The fix for "the app is never shown". */
  shown: {
    eyebrow: 'Built to get out of the way',
    title: 'The tools the club needs. Without the noise.',
    lede: 'No feed to maintain. No workout to upload. Just the event, the people, and the conversation.',

    eventCap: 'Event',
    // Coopers Rock (the demo event's location). Unsplash+ — Rocky Friz,
    // photo 0ZLjlmYB4Gk. Place/landscape, no faces (#3122 imagery rule).
    eventHeroImage: '/images/rocky-friz-0ZLjlmYB4Gk-unsplash.jpg',
    eventDay: '14',
    eventMonth: 'Mar',
    eventTitle: 'Saturday Long Run',
    eventTime: '6:40 AM',
    eventPlace: 'Coopers Rock lot',
    eventBlurb: 'Eight to twelve miles, easy pace up front. We wait at the top.',
    eventGoing: '12 going',
    eventOverflow: '+9',

    clubCap: 'Club',
    // Golden-hour group walking together, silhouetted (no legible faces) —
    // on-anchor "moving together, nobody the hero". Unsplash+ — Hester Qiang,
    // photo LhY8gh5_u_g (#3122 imagery rule).
    clubHeroImage: '/images/hester-qiang-LhY8gh5_u_g-unsplash.jpg',
    clubName: 'Golden Hour',
    clubMeta: 'Morgantown, WV · 34 members',
    clubLeaderTag: 'Leader',

    /**
     * The roster. Same people as the thread below, and they must stay the same
     * people: they were independently hardcoded in the page AND the proof
     * template, so an edit to one left the other showing a different club.
     * Caught on PR #3173 round 2.
     */
    members: [
      { name: 'Jamie R.', leader: true },
      { name: 'Marcus K.', leader: false },
      { name: 'Ana T.', leader: false },
    ],

    checkinCap: 'Check-in',
    checkinCount: '11',
    checkinLabel: 'checked in this morning',
    checkinBlurb:
      'Members tap once at the trailhead. Nobody takes attendance on a clipboard.',

    // "Discuss" is the app's own name for chat (the Discuss nav tab / LOBBY
    // room), so the demo cap matches what a member actually sees.
    threadCap: 'Discuss',

    /**
     * The thread itself. These ARE user-facing copy, so they live here rather
     * than inline in the page: inline JSX is outside check-voice.mjs's roots
     * (#2954) and outside gallery's round trip, which would leave the only
     * conversational prose on the page ungated and un-editable. Caught in
     * review of PR #3173.
     *
     * `from` is an INDEX INTO `members` above, not a name. Rounds 2 and 3 of
     * that review kept finding the same defect one field over: the roster and
     * the thread were two independent lists of the same three people, so
     * renaming a member through the proof left them still talking under the old
     * name. An index cannot disagree with the roster.
     *
     * It is a number rather than a string id on purpose. Galley makes every
     * STRING in this file editable and has no ignore list, so a string id would
     * appear in the proof as if it were copy and a well-meaning editor could
     * rename it and break the page. Numbers are invisible to the adapter, and
     * `as const` still resolves each one to a specific member at typecheck.
     *
     * `self: true` is the viewer's own bubble — a fact about whose screen this
     * is, not about the person, so it stays here. The leader tag is read from
     * the member record instead, which is where leadership actually lives.
     * Demo data (Golden Hour), never a real member.
     */
    thread: [
      { from: 1, text: 'Anyone parking at the lower lot? Gate was locked last week.', self: false, reactions: [] },
      { from: 2, text: "Upper lot is open. I'll be there 6:30 if anyone wants to warm up.", self: false, reactions: [{ emoji: '👍', count: 3 }] },
      { from: 0, text: 'Upper lot it is. I moved the pin on the event.', self: true, reactions: [{ emoji: '🙌', count: 2 }] },
    ],
  },

  /** Section 4 — three things it does. Type only, no icons. */
  things: {
    eyebrow: 'How it works',
    title: 'Make a plan. Notify the crew. Move on.',
    items: [
      {
        num: '01',
        heading: 'Post what\'s next.',
        body: 'A run, ride, walk, workout: whatever your club is doing. Put the when, where, and what-to-expect in one clear place.',
      },
      {
        num: '02',
        heading: 'Get the update.',
        body: 'Something change? Get a notification or update to your calendar automatically so you never miss out.',
      },
      {
        num: '03',
        heading: 'Move together.',
        body: 'Members can check in when they arrive. No clipboards or spreadsheets.',
      },
    ],
  },

  /** Section 5 — which one are you. */
  doors: {
    eyebrow: 'Two ways in',
    title: 'Which one are you?',
    leader: {
      heading: 'I run the club',
      body: 'You make plans and keep everyone pointed in the same direction.',
      cta: 'Getting started for leaders',
    },
    member: {
      heading: 'I show up',
      body: 'You want to know what\'s happening, where to be, and if something changed.',
      cta: 'What members get',
    },
  },

  /**
   * Section 6 — the door. The sign-in that currently OPENS the page, moved to
   * last. No cost language: #3122 D1 took "Free to start" off every public
   * page until ADR-0017 settles the plan.
   */
  close: {
    title: 'Where your club\'s plan lives.',
    body: 'Manage less. Move more.',
    cta: 'Sign in or sign up',
  },
} as const;
